import React, { useState } from 'react';
import { CheckCircle, X } from 'lucide-react';
import Button from '../../../components/ui/Button';
import { registrationService } from '../../../services/registrationService';
import { useAuth } from '../../../contexts/AuthContext';

const ApprovalModal = ({ registrationId, schoolName, onClose, onSuccess }) => {
  const [comments, setComments] = useState('');
  const [sendNotification, setSendNotification] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const { user } = useAuth();

  const handleApprove = async () => {
    if (!registrationId || !user?.id) {
      setError('Missing registration ID or user authentication');
      return;
    }

    setIsSubmitting(true);
    setError('');

    try {
      await registrationService.approveRegistration(registrationId, user.id, sendNotification);
      
      if (onSuccess) {
        onSuccess();
      }
      onClose();
    } catch (err) {
      console.error('Error approving registration:', err);
      setError(err?.message || 'Failed to approve registration. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-md w-full">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-6 h-6 text-green-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900">Approve Registration</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          <p className="text-gray-600 mb-6">
            You are about to approve the registration for{' '}
            <span className="font-semibold text-gray-900">{schoolName}</span>.
            This action will notify the school and allow them to participate in the tournament.
          </p>

          {/* Comments */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Comments (Optional)
            </label>
            <textarea
              value={comments}
              onChange={(e) => setComments(e?.target?.value)}
              placeholder="Add any comments or notes..."
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
              rows="4"
            />
          </div>

          {/* Notification Checkbox */}
          <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg mb-6">
            <input
              type="checkbox"
              id="sendNotification"
              checked={sendNotification}
              onChange={(e) => setSendNotification(e?.target?.checked)}
              className="mt-1 w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
            />
            <label htmlFor="sendNotification" className="text-sm text-gray-700">
              Send approval notification to school via email and SMS
            </label>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2.5 rounded-lg font-medium disabled:opacity-50"
            >
              Cancel
            </Button>
            <Button
              onClick={handleApprove}
              disabled={isSubmitting}
              className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2.5 rounded-lg font-medium flex items-center justify-center gap-2 disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Approving...
                </>
              ) : (
                <>
                  <CheckCircle className="w-5 h-5" />
                  Approve Registration
                </>
              )}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApprovalModal;